import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { projects as projectsApi } from "../services/api";
import { ProjectStatsTab } from "./ProjectsPage";
import { FiBarChart2, FiArrowRight, FiHome } from "react-icons/fi";
import toast from "react-hot-toast";

// ═══════════════════════════════════════════════════════════════════
// AnalyticsPage — صفحة التحليل المالي
// Routes:
//   /analytics                  → لائحة المشاريع + بطاقة المحفظة
//   /analytics/portfolio        → إحصائيات المحفظة الكاملة
//   /analytics/project/:id      → إحصائيات مشروع واحد
// ═══════════════════════════════════════════════════════════════════

export default function AnalyticsPage() {
  const { mode, id } = useParams();
  const navigate = useNavigate();
  
  // Determine which view to show based on URL
  // /analytics              → mode = undefined
  // /analytics/portfolio    → mode = "portfolio"
  // /analytics/project/:id  → mode = "project"
  
  if (mode === "portfolio") {
    return <PortfolioAnalyticsView onBack={() => navigate("/analytics")} />;
  }
  
  if (mode === "project" && id) {
    return <SingleProjectAnalyticsView projectId={id} onBack={() => navigate("/analytics")} />;
  }
  
  // Default: list of projects + portfolio card
  return <AnalyticsList onSelectProject={(pid) => navigate(`/analytics/project/${pid}`)} onSelectPortfolio={() => navigate("/analytics/portfolio")} />;
}

// ═══════════════════════════════════════════════════════════════════
// AnalyticsList — قائمة الاختيار
// ═══════════════════════════════════════════════════════════════════
function AnalyticsList({ onSelectProject, onSelectPortfolio }) {
  const [projectsList, setProjectsList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    projectsApi.list()
      .then(r => { setProjectsList(r.data.projects || []); setLoading(false); })
      .catch(() => { toast.error("فشل تحميل المشاريع"); setLoading(false); });
  }, []);

  if (loading) return (
    <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>جاري التحميل...</div>
  );

  return (
    <div style={{ padding: 24, direction: "rtl", maxWidth: 1400, margin: "0 auto" }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: "#1e293b", margin: 0, display: "flex", alignItems: "center", gap: 10 }}>
          <FiBarChart2 size={26} color="#16a34a" />
          التحليل المالي
        </h1>
        <p style={{ fontSize: 13, color: "#64748b", marginTop: 6 }}>
          اختر مشروعاً لعرض إحصائياته التفصيلية، أو اعرض إحصائيات محفظة المشاريع كاملة.
        </p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 }}>
        {/* Portfolio Card */}
        <div onClick={onSelectPortfolio}
          style={{
            background: "linear-gradient(135deg, #1e40af, #2563eb)",
            color: "#fff",
            borderRadius: 14,
            padding: 20,
            cursor: "pointer",
            transition: "transform 0.15s, box-shadow 0.15s",
            boxShadow: "0 4px 12px rgba(37,99,235,0.25)",
            position: "relative",
            overflow: "hidden",
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(37,99,235,0.35)"; }}
          onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(37,99,235,0.25)"; }}
        >
          <div style={{ position: "absolute", top: -20, right: -20, fontSize: 100, opacity: 0.1 }}>📊</div>
          <div style={{ position: "relative" }}>
            <div style={{ fontSize: 11, opacity: 0.85, fontWeight: 600, marginBottom: 6 }}>عرض موحد</div>
            <h3 style={{ fontSize: 18, fontWeight: 800, margin: 0, marginBottom: 8 }}>محفظة المشاريع</h3>
            <p style={{ fontSize: 12, opacity: 0.9, margin: 0, marginBottom: 14, lineHeight: 1.5 }}>
              إحصائيات مجمعة من كل المشاريع — قيم إجمالية، فئات، ومقارنات
            </p>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 12, borderTop: "1px solid rgba(255,255,255,0.2)" }}>
              <span style={{ fontSize: 12, fontWeight: 600 }}>{projectsList.length} مشروع</span>
              <FiArrowRight size={18} />
            </div>
          </div>
        </div>

        {/* Individual Project Cards */}
        {projectsList.map(p => {
          const total = parseInt(p.total_units) || 0;
          const sold = parseInt(p.sold_units) || 0;
          const reserved = parseInt(p.reserved_units) || 0;
          const avail = parseInt(p.available_units) || 0;
          const pct = total > 0 ? Math.round(((sold + reserved) / total) * 100) : 0;
          
          return (
            <div key={p.id} onClick={() => onSelectProject(p.id)}
              style={{
                background: "#fff",
                border: "1px solid #e2e8f0",
                borderRadius: 14,
                padding: 18,
                cursor: "pointer",
                transition: "all 0.15s",
                boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 8px 20px rgba(0,0,0,0.08)"; e.currentTarget.style.borderColor = "#bbf7d0"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.04)"; e.currentTarget.style.borderColor = "#e2e8f0"; }}
            >
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 10 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>🏢</div>
                <div style={{ flex: 1 }}>
                  <h3 style={{ fontSize: 15, fontWeight: 700, color: "#1e293b", margin: 0 }}>{p.name}</h3>
                  {p.developer_name && <p style={{ fontSize: 11, color: "#64748b", margin: "2px 0 0" }}>{p.developer_name}</p>}
                </div>
              </div>
              
              {/* Stats grid */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6, marginBottom: 10 }}>
                <Stat label="إجمالي" value={total} color="#64748b" />
                <Stat label="متاح" value={avail} color="#16a34a" />
                <Stat label="محجوز" value={reserved} color="#2563eb" />
                <Stat label="مُباع" value={sold} color="#dc2626" />
              </div>
              
              {/* Progress bar */}
              <div style={{ background: "#f1f5f9", borderRadius: 4, height: 6, overflow: "hidden", marginBottom: 6 }}>
                <div style={{ width: `${pct}%`, height: "100%", background: "linear-gradient(90deg, #16a34a, #22c55e)", borderRadius: 4 }} />
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 11, color: "#64748b" }}>
                <span>تصريف {pct}%</span>
                <FiArrowRight size={14} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div style={{ textAlign: "center", background: "#f8fafc", borderRadius: 6, padding: "6px 4px" }}>
      <div style={{ fontSize: 14, fontWeight: 800, color }}>{value}</div>
      <div style={{ fontSize: 9, color: "#94a3b8" }}>{label}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// SingleProjectAnalyticsView — إحصائيات مشروع واحد
// ═══════════════════════════════════════════════════════════════════
function SingleProjectAnalyticsView({ projectId, onBack }) {
  const [project, setProject] = useState(null);
  const [units, setUnits] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      projectsApi.get(projectId),
      projectsApi.getBuildings(projectId),
    ]).then(([projRes, bldRes]) => {
      setProject(projRes.data.project);
      setUnits(projRes.data.units || []);
      setBuildings(bldRes.data.buildings || []);
      setLoading(false);
    }).catch(() => {
      toast.error("فشل تحميل بيانات المشروع");
      setLoading(false);
    });
  }, [projectId]);

  if (loading) return (
    <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>جاري التحميل...</div>
  );

  if (!project) return (
    <div style={{ padding: 40, textAlign: "center", color: "#dc2626" }}>المشروع غير موجود</div>
  );

  return (
    <div style={{ direction: "rtl", display: "flex", flexDirection: "column", height: "100vh" }}>
      {/* Top breadcrumb */}
      <div style={{ padding: "14px 24px", background: "#fff", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", gap: 12 }}>
        <button onClick={onBack} style={{
          padding: "6px 14px", background: "#f1f5f9", border: "none", borderRadius: 7,
          cursor: "pointer", fontFamily: "inherit", fontSize: 12, color: "#475569",
          display: "flex", alignItems: "center", gap: 6,
        }}>
          ‹ رجوع
        </button>
        <div style={{ fontSize: 12, color: "#94a3b8" }}>التحليل المالي / </div>
        <h2 style={{ fontSize: 16, fontWeight: 800, color: "#1e293b", margin: 0 }}>
          📊 {project.name}
        </h2>
      </div>
      
      {/* Stats content */}
      <div style={{ flex: 1, overflow: "auto" }}>
        <ProjectStatsTab units={units} buildings={buildings} project={project} />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// PortfolioAnalyticsView — إحصائيات محفظة المشاريع كاملة
// ═══════════════════════════════════════════════════════════════════
function PortfolioAnalyticsView({ onBack }) {
  const [projectsList, setProjectsList] = useState([]);
  const [allUnits, setAllUnits] = useState([]);
  const [allBuildings, setAllBuildings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    projectsApi.list().then(async (lr) => {
      const pList = lr.data.projects || [];
      setProjectsList(pList);
      // Fetch units + buildings for each project in parallel
      try {
        const results = await Promise.all(
          pList.map(p => Promise.all([
            projectsApi.get(p.id).catch(() => ({ data: { units: [] } })),
            projectsApi.getBuildings(p.id).catch(() => ({ data: { buildings: [] } })),
          ]))
        );
        const combinedUnits = [];
        const combinedBuildings = [];
        results.forEach(([projRes, bldRes], idx) => {
          const proj = pList[idx];
          (projRes.data.units || []).forEach(u => {
            combinedUnits.push({ ...u, _projectId: proj.id, _projectName: proj.name });
          });
          (bldRes.data.buildings || []).forEach(b => {
            combinedBuildings.push({ ...b, _projectId: proj.id, _projectName: proj.name });
          });
        });
        setAllUnits(combinedUnits);
        setAllBuildings(combinedBuildings);
      } catch (e) {
        toast.error("فشل تحميل بعض البيانات");
      } finally {
        setLoading(false);
      }
    }).catch(() => { toast.error("فشل تحميل المشاريع"); setLoading(false); });
  }, []);

  if (loading) return (
    <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>
      <div style={{ fontSize: 14, marginBottom: 10 }}>جاري تجميع بيانات المحفظة...</div>
      <div style={{ fontSize: 11, color: "#cbd5e1" }}>قد يأخذ بضع ثوان لتحميل كل المشاريع</div>
    </div>
  );

  // Synthetic project for the portfolio view
  const portfolioProject = {
    id: 0,
    name: "محفظة المشاريع — رئال للتطوير العقاري",
    developer_name: `${projectsList.length} مشروع`,
  };

  return (
    <div style={{ direction: "rtl", display: "flex", flexDirection: "column", height: "100vh" }}>
      <div style={{ padding: "14px 24px", background: "linear-gradient(90deg, #1e40af, #2563eb)", color: "#fff", display: "flex", alignItems: "center", gap: 12 }}>
        <button onClick={onBack} style={{
          padding: "6px 14px", background: "rgba(255,255,255,0.2)", border: "none", borderRadius: 7,
          cursor: "pointer", fontFamily: "inherit", fontSize: 12, color: "#fff",
          display: "flex", alignItems: "center", gap: 6,
        }}>
          ‹ رجوع
        </button>
        <div style={{ fontSize: 12, opacity: 0.85 }}>التحليل المالي / </div>
        <h2 style={{ fontSize: 16, fontWeight: 800, margin: 0 }}>
          📊 محفظة المشاريع ({projectsList.length} مشروع، {allUnits.length} وحدة)
        </h2>
      </div>
      
      <div style={{ flex: 1, overflow: "auto" }}>
        <ProjectStatsTab units={allUnits} buildings={allBuildings} project={portfolioProject} />
      </div>
    </div>
  );
}
